/**
 * Global Behavior Signal Provider
 *
 * Initialize this once at the app root to enable behavior signal collection
 * on all API requests automatically.
 *
 * Usage in gatsby-browser.js or your root layout:
 *
 *   import { initGlobalBehaviorSignal } from './components/BehaviorSignalProvider';
 *   initGlobalBehaviorSignal();
 */

import { initBehaviorSignal } from '../utils/behaviorSignal.mjs';

let isInitialized = false;

/**
 * Initialize behavior signal collection globally
 *
 * Call this once when your app starts (e.g., in gatsby-browser.js or Layout component)
 *
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Enable/disable collection (default: true in production)
 * @param {boolean} options.debug - Enable debug logging (default: false)
 * @param {number} options.throttleMs - Throttle interval for pointer/scroll events (default: 100)
 */
export function initGlobalBehaviorSignal(options = {}) {
  // Only initialize once
  if (isInitialized) {
    return;
  }

  // Check if we're in a browser environment
  if (typeof window === 'undefined') {
    return;
  }

  const {
    enabled = process.env.NODE_ENV === 'production',
    debug = false,
    throttleMs = 100,
  } = options;

  if (enabled) {
    initBehaviorSignal({
      enabled: true,
      debug,
      throttleMs,
    });

    isInitialized = true;

    if (debug) {
      console.log('[BehaviorSignal] Global collector initialized');
    }
  }
}

/**
 * Check if the global behavior signal collector is initialized
 *
 * @returns {boolean}
 */
export function isGlobalBehaviorSignalInitialized() {
  return isInitialized;
}
